import { useEffect, useRef, useState } from "react";
import { HiOutlineRefresh, HiOutlineExclamationCircle, HiOutlineCheckCircle, HiOutlineSearch, HiOutlineX } from "react-icons/hi";
import { fetchSyncLogs, fetchSyncLogsSummary } from "../services/masterDataApi";
import Toast from "../components/Toast";
import Pagination from "../components/Pagination";
import FilterDropdown from "../components/FilterDropdown";
import { useDebounce } from "../hooks/useDebounce";

function SyncLogs() {
 const [logs, setLogs] = useState([]);
 const [summary, setSummary] = useState(null);
 const [loading, setLoading] = useState(true);
 const [toast, setToast] = useState(null);
 const [page, setPage] = useState(1);
 const [pageSize, setPageSize] = useState(20);
 const [totalCount, setTotalCount] = useState(0);
 const [totalPages, setTotalPages] = useState(1);
 const [search, setSearch] = useState("");
 const debouncedSearch = useDebounce(search, 400);
 const [filterStatus, setFilterStatus] = useState("");
 const [filterSyncType, setFilterSyncType] = useState("");

 const loadLogs = async () => {
  setLoading(true);
  try {
   const logsRes = await fetchSyncLogs({ status: filterStatus || undefined, sync_type: filterSyncType || undefined, search: debouncedSearch, page, page_size: pageSize });
   setLogs(logsRes.data || []);
   setTotalCount(logsRes.total_count || 0);
   setTotalPages(logsRes.total_pages || 1);
  } catch (err) {
   setToast({ type: "error", message: err.response?.data?.detail || "Failed to load sync logs" });
  } finally {
   setLoading(false);
  }
 };

 // The summary cards ignore every filter — load them on open and on Refresh
 // only, not on each page/filter/search change.
 const loadSummary = async () => {
  try {
   const summaryRes = await fetchSyncLogsSummary();
   setSummary(summaryRes.data || null);
  } catch (err) {
   setToast({ type: "error", message: err.response?.data?.detail || "Failed to load sync logs" });
  }
 };

 const loadData = () => {
  loadLogs();
  loadSummary();
 };

 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { loadSummary(); }, []);

 // A filter/search change restarts paging. Reset the page *instead of*
 // loading when not already on page 1, so only one request fires.
 const filterKey = `${filterStatus}|${filterSyncType}|${debouncedSearch}`;
 const lastFilterKeyRef = useRef(filterKey);
 useEffect(() => {
  if (lastFilterKeyRef.current !== filterKey) {
   lastFilterKeyRef.current = filterKey;
   if (page !== 1) { setPage(1); return; }
  }
  loadLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [page, pageSize, filterKey]);

 const handlePageSizeChange = (newSize) => {
  setPageSize(newSize);
  setPage(1);
 };

 return (
  <div className="p-4 sm:p-6 max-w-7xl mx-auto">
   {toast && <Toast {...toast} onClose={() => setToast(null)} />}

   <div className="flex items-center justify-between mb-5">
    <div>
     <h1 className="text-xl font-bold text-[#24292f]">Sync Logs</h1>
     <p className="text-sm text-[#57606a] mt-0.5">Monitor school data synchronization status</p>
    </div>
    <button onClick={loadData} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6]">
     <HiOutlineRefresh className="text-sm" /> Refresh
    </button>
   </div>

   {/* Summary Cards */}
   {summary && (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
     <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
      <p className="text-xs text-[#8b949e] mb-1">Total Logs</p>
      <p className="text-2xl font-bold text-[#24292f]">{summary.total_logs}</p>
     </div>
     <div className="bg-white rounded-xl border border-red-200 p-4">
      <p className="text-xs text-red-500 mb-1">Failed</p>
      <p className="text-2xl font-bold text-red-600">{summary.failed}</p>
     </div>
     <div className="bg-white rounded-xl border border-green-200 p-4">
      <p className="text-xs text-green-500 mb-1">Success</p>
      <p className="text-2xl font-bold text-green-600">{summary.success}</p>
     </div>
     <div className="bg-white rounded-xl border border-orange-200 p-4">
      <p className="text-xs text-orange-500 mb-1">Failed Schools</p>
      <p className="text-2xl font-bold text-orange-600">{summary.failed_schools}</p>
     </div>
    </div>
   )}

   {/* Table */}
   <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-x-auto">
    {/* Toolbar */}
    <div className="flex justify-between items-center px-6 py-4 border-b border-[#e5e7eb] flex-wrap gap-3">
     <div className="relative flex items-center flex-1 max-w-sm">
      <HiOutlineSearch className="absolute left-3.5 text-[#8b949e] text-sm" />
      <input
       type="text"
       className="w-full py-2.5 pr-8 pl-10 text-sm border border-[#d0d5dd] rounded-md text-[#24292f] bg-white transition-all duration-200 box-border placeholder:text-[#8b949e] focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10"
       placeholder="Search by school, UDISE, or synced by…"
       value={search}
       onChange={(e) => setSearch(e.target.value)}
      />
      {search && (
       <button
        className="absolute right-2 bg-transparent border-none text-[#8b949e] cursor-pointer p-1 rounded-md hover:text-[#57606a] hover:bg-[#f3f4f6] transition-colors"
        onClick={() => setSearch("")}
       >
        <HiOutlineX className="text-sm" />
       </button>
      )}
     </div>
     <FilterDropdown
      filters={[
       {
        key: "sync_type",
        label: "Sync Type",
        type: "select",
        options: [
         { value: "pull", label: "Pull" },
         { value: "push", label: "Push" },
        ],
       },
       {
        key: "status",
        label: "Status",
        type: "select",
        options: [
         { value: "failed", label: "Failed" },
         { value: "success", label: "Success" },
        ],
       },
      ]}
      values={{ sync_type: filterSyncType, status: filterStatus }}
      onChange={(next) => {
       setFilterSyncType(next.sync_type || "");
       setFilterStatus(next.status || "");
      }}
     />
    </div>

    <table className="w-full text-sm">
     <thead>
      <tr className="bg-[#f6f8fa] border-b border-[#e5e7eb]">
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">School</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">UDISE</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Sync Type</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Synced By</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Status</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Error</th>
       <th className="text-left px-4 py-3 font-semibold text-[#24292f]">Time</th>
      </tr>
     </thead>
     <tbody>
      {loading ? (
       <tr><td colSpan={7} className="text-center py-12 text-[#8b949e]">Loading…</td></tr>
      ) : logs.length === 0 ? (
       <tr><td colSpan={7} className="text-center py-12 text-[#8b949e]">No sync logs found.</td></tr>
      ) : (
       logs.map((log) => (
        <tr key={log.id} className="border-b border-[#f0f0f0] hover:bg-[#f6f8fa]">
         <td className="px-4 py-3 font-medium text-[#24292f]">{log.school_name || `School #${log.school_id}`}</td>
         <td className="px-4 py-3 text-[#57606a]">{log.udise_code || "—"}</td>
         <td className="px-4 py-3 text-[#57606a] capitalize">{log.sync_type}</td>
         <td className="px-4 py-3 text-[#57606a]">{log.synced_by || "—"}</td>
         <td className="px-4 py-3">
          {log.status === "failed" ? (
           <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
            <HiOutlineExclamationCircle className="text-xs" /> Failed
           </span>
          ) : (
           <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
            <HiOutlineCheckCircle className="text-xs" /> Success
           </span>
          )}
         </td>
         <td className="px-4 py-3 text-[#57606a] text-xs max-w-xs truncate" title={log.error_message || ""}>
          {log.error_message || "—"}
         </td>
         <td className="px-4 py-3 text-[#57606a] text-xs whitespace-nowrap">
          {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
         </td>
        </tr>
       ))
      )}
     </tbody>
    </table>

    <Pagination
     currentPage={page}
     totalPages={totalPages}
     totalCount={totalCount}
     pageSize={pageSize}
     onPageChange={setPage}
     onPageSizeChange={handlePageSizeChange}
     entityName="sync logs"
    />
   </div>
  </div>
 );
}

export default SyncLogs;
